import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { api } from "../lib/api";
import { Button, ErrorNote } from "./primitives";

/**
 * "Submit an issue", in the header on every page (#118).
 *
 * For the beta: the quickest route from "that looked wrong" to somebody who can
 * fix it. The page is filled in from the address bar and stays editable,
 * because the page somebody reports from is not always the one it happened on.
 *
 * The server records the report before it mails anybody, so the confirmation
 * says which of the two happened — "sent" only when an email actually went.
 */
export function ReportIssue() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // The bar's own form, like a nav item: it was the one outlined button
        // in a row of text, which made the least-used control the loudest.
        className="h-control-sm shrink-0 whitespace-nowrap rounded-lg px-2.5 text-sm text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
      >
        Submit an issue
      </button>
      {open && <IssueDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function IssueDialog({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const [text, setText] = useState("");
  const [page, setPage] = useState(`${location.pathname}${location.search}`);

  const report = useMutation({
    mutationFn: () => api.reportIssue({ text: text.trim(), page: page.trim() }),
  });

  // Escape closes, as every dialog does — but not mid-send, or a report the
  // server is still taking would look abandoned.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !report.isPending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, report.isPending]);

  const done = report.isSuccess;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-plane/80 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !report.isPending) onClose();
      }}
    >
      <form
        className="w-full max-w-lg rounded-lg border border-line bg-surface shadow-lg"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim()) report.mutate();
        }}
      >
        <div className="border-b border-line px-5 py-4">
          <h2 id="issue-title" className="text-base font-semibold text-ink">
            Submit an issue
          </h2>
        </div>

        {done ? (
          <div className="px-5 py-5 text-sm text-ink-2">
            {report.data.emailed > 0
              ? "Thank you — it has been sent to the administrators."
              : "Thank you — it has been recorded for the administrators."}
          </div>
        ) : (
          <div className="space-y-4 px-5 py-5">
            <div>
              <label className="field-label" htmlFor="issue-text">
                What happened?
              </label>
              <textarea
                id="issue-text"
                className="field min-h-32"
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="What you did, what you expected, and what you saw instead."
                maxLength={5000}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="issue-page">
                On which page?
              </label>
              <input
                id="issue-page"
                className="field font-mono text-xs"
                value={page}
                onChange={(event) => setPage(event.target.value)}
                maxLength={500}
              />
            </div>
            {report.isError && <ErrorNote error={report.error} />}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-line px-5 py-4">
          {done ? (
            <Button type="button" variant="primary" onClick={onClose} autoFocus>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={onClose} disabled={report.isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!text.trim() || report.isPending}
              >
                {report.isPending ? "Sending…" : "OK"}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
